/**
 * Hover video media — video behavior for the `hover-video-media` snippet.
 * Desktop: play on hover, reset to the beginning on mouseout.
 * Mobile: autoplay the most visible item as it scrolls into view (one at a time per section).
 * Videos play once, then reset. Retrigger by scrolling or hovering again.
 */
(function () {
  'use strict';

  var DEFAULT_BREAKPOINT = 1200;
  var VISIBILITY_THRESHOLD = 0.4;
  var SELECTOR = '.hover-video-media';
  var INITIALIZED_ATTRIBUTE = 'data-hover-video-initialized';

  /** Above the item's breakpoint the layout is a grid, so hover is the trigger instead of scroll. */
  function isDesktop(media) {
    var breakpoint = parseInt(media.getAttribute('data-hover-video-breakpoint'), 10);

    if (isNaN(breakpoint)) breakpoint = DEFAULT_BREAKPOINT;

    return window.innerWidth >= breakpoint;
  }

  function setupLoadingStates(media, video) {
    video.addEventListener('waiting', function () {
      media.classList.add('is-loading');
      media.classList.remove('is-ready');
    });

    video.addEventListener('canplay', function () {
      media.classList.remove('is-loading');
      media.classList.add('is-ready');
    });

    if (video.readyState >= 3) {
      media.classList.add('is-ready');
    }

    video.addEventListener('ended', function () {
      video.currentTime = 0;
      media.classList.remove('is-playing');
      media.classList.remove('is-loading');
    });
  }

  function playVideo(media, video) {
    // Allow retrigger — only skip if actively playing right now
    if (!video.paused && !video.ended) return;

    video.currentTime = 0;
    media.classList.add('is-loading');

    var playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(function () {
          media.classList.remove('is-loading');
          media.classList.add('is-playing');
        })
        .catch(function () {
          media.classList.remove('is-loading');
        });
    } else {
      media.classList.add('is-playing');
    }
  }

  function stopVideo(media, video) {
    video.pause();
    video.currentTime = 0;
    media.classList.remove('is-playing');
    media.classList.remove('is-loading');
  }

  /**
   * Share of the element's area inside the viewport. Both axes are measured so that items in a
   * horizontal scroller (e.g. the mobile multi-column carousel) are ranked by how far they've been
   * scrolled into view, not just by their vertical position.
   */
  function getVisibleRatio(element) {
    var rect = element.getBoundingClientRect();
    var viewportHeight = window.innerHeight;
    var viewportWidth = window.innerWidth;

    if (rect.height === 0 || rect.width === 0) return 0;

    var visibleHeight = Math.min(viewportHeight, rect.bottom) - Math.max(0, rect.top);
    var visibleWidth = Math.min(viewportWidth, rect.right) - Math.max(0, rect.left);

    if (visibleHeight <= 0 || visibleWidth <= 0) return 0;

    return (visibleHeight * visibleWidth) / (rect.height * rect.width);
  }

  /**
   * Mobile scroll playback is scoped per group so that sibling items in the same
   * section never play at once, while separate sections stay independent.
   */
  function createScrollController(items) {
    var currentlyPlaying = null;
    var ticking = false;

    function stopCurrent() {
      if (!currentlyPlaying) return;
      stopVideo(currentlyPlaying.media, currentlyPlaying.video);
      currentlyPlaying = null;
    }

    function check() {
      ticking = false;

      var best = null;
      var bestRatio = 0;

      items.forEach(function (item) {
        if (isDesktop(item.media)) return;
        // Skip media inside hidden UI (e.g. a closed drawer panel), which still has layout boxes.
        if (item.media.checkVisibility && !item.media.checkVisibility({ visibilityProperty: true, opacityProperty: true })) return;

        var ratio = getVisibleRatio(item.media);

        if (ratio >= VISIBILITY_THRESHOLD && ratio > bestRatio) {
          bestRatio = ratio;
          best = item;
        }
      });

      if (best && best !== currentlyPlaying) {
        stopCurrent();
        playVideo(best.media, best.video);
        currentlyPlaying = best;
      } else if (!best) {
        stopCurrent();
      }
    }

    return function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    };
  }

  function init(root) {
    var scope = root || document;
    var candidates = scope.querySelectorAll(SELECTOR);
    var groups = new Map();

    candidates.forEach(function (media) {
      if (media.hasAttribute(INITIALIZED_ATTRIBUTE)) return;

      var video = media.querySelector('video');
      if (!video) return;

      media.setAttribute(INITIALIZED_ATTRIBUTE, 'true');
      setupLoadingStates(media, video);

      // An optional ancestor (e.g. a whole nav card) can own the hover instead of the media itself.
      var hoverTargetSelector = media.getAttribute('data-hover-video-target');
      var hoverTarget = (hoverTargetSelector && media.closest(hoverTargetSelector)) || media;

      hoverTarget.addEventListener('mouseenter', function () {
        if (!isDesktop(media)) return;
        playVideo(media, video);
      });

      hoverTarget.addEventListener('mouseleave', function () {
        if (!isDesktop(media)) return;
        stopVideo(media, video);
      });

      var group = media.closest('.shopify-section') || document;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push({ media: media, video: video });
    });

    groups.forEach(function (items) {
      var onScroll = createScrollController(items);

      // Captured on the document so scrolling an inner container (the mobile carousel) counts too —
      // scroll events don't bubble, but they are observable during the capture phase.
      document.addEventListener('scroll', onScroll, { passive: true, capture: true });
      window.addEventListener('resize', onScroll, { passive: true });
      onScroll();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();
